import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scale, Clock, AlertCircle } from 'lucide-react';
import Navigation from '../../components/Navigation';
import { supabase } from '../../lib/supabase';
import ContentRenderer from '../uputstva/components/ContentRenderer';
import ContentSkeleton from '../uputstva/components/ContentSkeleton';
import { useTranslation } from 'react-i18next';

const UsloviKoriscenjaPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [content, setContent] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase?.from('platform_content')?.select('content, updated_at')?.eq('content_type', 'terms')?.single();

      if (fetchError) throw fetchError;
      setContent(data?.content || '');
      setUpdatedAt(data?.updated_at || null);
    } catch (err) {
      setError(t('terms.unavailable'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const locale = i18n?.language === 'en' ? 'en-GB' : 'sr-RS';
    return new Date(dateStr)?.toLocaleDateString(locale, {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navigation />
      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('terms.back')}
        </button>

        <div className="flex items-start gap-4 mb-8">
          <div className="p-3 bg-blue-100 rounded-xl flex-shrink-0">
            <Scale className="w-7 h-7 text-blue-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('terms.title')}</h1>
            <p className="text-gray-500 mt-1 text-sm">{t('terms.subtitle')}</p>
            {updatedAt && (
              <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-400">
                <Clock className="w-3.5 h-3.5" />
                <span>{t('terms.lastUpdated')} {formatDate(updatedAt)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          {loading ? (
            <ContentSkeleton />
          ) : error ? (
            <div className="p-8 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          ) : (
            <ContentRenderer content={content} />
          )}
        </div>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-700 leading-relaxed">
            {t('terms.notice')}
          </p>
        </div>
      </main>
      <footer className="bg-white border-t border-gray-200 py-6 mt-8">
        <div className="max-w-3xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-400">{t('terms.copyright', { year: new Date()?.getFullYear() })}</p>
          <div className="flex items-center gap-4 text-xs">
            <a href="/uputstva" className="text-gray-500 transition-colors" style={{}} onMouseEnter={e => e.currentTarget.style.color='#A8922A'} onMouseLeave={e => e.currentTarget.style.color=''}>{t('terms.footerInstructions')}</a>
            <a href="/register" className="text-gray-500 transition-colors" onMouseEnter={e => e.currentTarget.style.color='#A8922A'} onMouseLeave={e => e.currentTarget.style.color=''}>{t('terms.footerRegister')}</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UsloviKoriscenjaPage;
